"use client";

import { c as _c } from "react/compiler-runtime";
import { Download01Icon, LogoutSquare01Icon, Settings02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQueryClient } from "@tanstack/react-query";
import type { Route } from "next";
import Link from "next/link";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useFCM } from "@/hooks/use-fcm";
import { usePwaInstall } from "@/hooks/use-pwa-install";
import { authClient } from "@/lib/auth-client";
import { clearClientSessionArtifacts } from "@/lib/session/logout-cleanup";
export default function UserMenu() {
  const $ = _c(49);
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const {
    data: session,
    isPending
  } = authClient.useSession();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      preventInit: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    cleanupForLogout
  } = useFCM(t0);
  const {
    canPromptNatively,
    promptInstall,
    requestShow,
    support
  } = usePwaInstall();
  const showInstallMenuItem = false || support === "native" && canPromptNatively || support === "ios-safari" || support === "unsupported";
  if (isPending) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Skeleton className="h-9 w-9 rounded-full" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (!session) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Link href={"/auth/login" as Route}><Button variant="default" size="lg" className="px-6">Get Started</Button></Link>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== session.user.name) {
    t1 = session.user.name?.trim() || "current user";
    $[3] = session.user.name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = `Open account menu for ${t1}`;
  const t3 = session.user.handle ?? session.user.id;
  let t4;
  if ($[5] !== session.user.image || $[6] !== session.user.name || $[7] !== t3) {
    t4 = <UserAvatar className="h-9 w-9 transition-all duration-300 hover:opacity-80" image={session.user.image} name={session.user.name} seed={t3} />;
    $[5] = session.user.image;
    $[6] = session.user.name;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <DropdownMenuTrigger aria-label={t2}>{t4}</DropdownMenuTrigger>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== session.user.name) {
    t6 = <p className="line-clamp-1 truncate font-medium text-foreground/90 text-sm leading-none">{session.user.name}</p>;
    $[12] = session.user.name;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = session.user.phoneNumber || session.user.email;
  let t8;
  if ($[14] !== t7) {
    t8 = <p className="text-muted-foreground text-xs leading-none">{t7}</p>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t8) {
    t9 = <DropdownMenuGroup><DropdownMenuLabel className="font-normal"><div className="flex flex-col space-y-1">{t6}{t8}</div></DropdownMenuLabel></DropdownMenuGroup>;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DropdownMenuSeparator />;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsOpen(false);
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DropdownMenuGroup><DropdownMenuItem onClick={t11}><Link href="/settings" className="flex h-full w-full cursor-pointer items-center justify-start"><HugeiconsIcon icon={Settings02Icon} className="mr-3 h-4 w-4" /><span>Settings</span></Link></DropdownMenuItem></DropdownMenuGroup>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== canPromptNatively || $[23] !== promptInstall || $[24] !== requestShow || $[25] !== showInstallMenuItem || $[26] !== support) {
    t13 = showInstallMenuItem ? <><DropdownMenuSeparator /><DropdownMenuGroup><DropdownMenuItem className="cursor-pointer text-blue-600 focus:text-blue-600 dark:text-blue-400 dark:focus:text-blue-400" onClick={async () => {
          setIsOpen(false);
          if (support === "native" && canPromptNatively) {
            await promptInstall();
            return;
          }
          if (false && support === "unknown") {
            return;
          }
          requestShow();
        }}><HugeiconsIcon icon={Download01Icon} className="h-4 w-4" /><span>Install App</span></DropdownMenuItem></DropdownMenuGroup></> : null;
    $[22] = canPromptNatively;
    $[23] = promptInstall;
    $[24] = requestShow;
    $[25] = showInstallMenuItem;
    $[26] = support;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DropdownMenuSeparator />;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== cleanupForLogout || $[30] !== isSigningOut || $[31] !== queryClient || $[32] !== session.user.id) {
    t15 = async () => {
      if (isSigningOut) {
        return;
      }
      setIsSigningOut(true);
      setIsOpen(false);
      ;
      try {
        ;
        try {
          await cleanupForLogout();
        } catch (t17) {
          const error_0 = t17;
          console.error("Push token cleanup failed during logout", error_0);
        }
        await authClient.signOut({
          fetchOptions: {
            onSuccess: async () => {
              await clearClientSessionArtifacts({
                queryClient,
                userId: session.user.id
              });
              if (typeof window !== "undefined") {
                window.location.assign("/");
              }
            }
          }
        });
      } catch (t16) {
        const error = t16;
        setIsSigningOut(false);
        toast.error("Failed to log out", {
          description: error instanceof Error ? error.message : "Please try again."
        });
      }
    };
    $[29] = cleanupForLogout;
    $[30] = isSigningOut;
    $[31] = queryClient;
    $[32] = session.user.id;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <HugeiconsIcon icon={LogoutSquare01Icon} className="mr-1 h-4 w-4" />;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = isSigningOut ? "Logging out..." : "Log out";
  let t18;
  if ($[35] !== t17) {
    t18 = <span>{t17}</span>;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== isSigningOut || $[38] !== t15 || $[39] !== t18) {
    t19 = <DropdownMenuItem className="h-full w-full cursor-pointer text-red-600" disabled={isSigningOut} onClick={t15}>{t16}{t18}</DropdownMenuItem>;
    $[37] = isSigningOut;
    $[38] = t15;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t13 || $[42] !== t19 || $[43] !== t9) {
    t20 = <DropdownMenuContent className="w-56 font-jakarta" align="end">{t9}{t10}{t12}{t13}{t14}{t19}</DropdownMenuContent>;
    $[41] = t13;
    $[42] = t19;
    $[43] = t9;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== isOpen || $[46] !== t20 || $[47] !== t5) {
    t21 = <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>{t5}{t20}</DropdownMenu>;
    $[45] = isOpen;
    $[46] = t20;
    $[47] = t5;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  return t21;
}